import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import CardComponent, { StyledCard } from '../Card.component';
import IconButtonComponent, { StyledIconButton } from '../IconButton.component';
import CrossIcon from '../Icons/Cross.icon';
import { useModalManager } from '../../hooks/ModalManager.hook';
import { setDispatchModal } from '../../redux/App/App.slice';
import { useAppDispatch } from '../../hooks/useAppDispatcher.hook';

export interface BaseModalProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  leftBlock?: React.ReactNode;
  rightBlock?: React.ReactNode;
  hasHeader?: boolean;
  isBlocked?: boolean;
  onClose?: () => void;
  onCrossButtonClick?: () => void;
}

const StyledBaseModalHeaderTitle = styled.div`
  font-weight: 500;
  font-size: 16px;
  line-height: 28px;
  overflow: hidden;
`;
const StyledBaseModalHeaderBorder = css`
  border-bottom: 1px solid ${({ theme }) => theme.colors.BORDER};
`;
const StyledBaseModalFooter = styled.div`
  padding: 0 15px 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.BORDER};
`;
export const StyledBaseModalContent = styled.div`
  padding: 10px 15px;
  /*overflow: auto;*/
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;
export const StyledBaseModalHeader = styled.div<{
  withBorder: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 10px 10px 15px;

  & > ${StyledIconButton} {
    z-index: 9999;
  }

  ${props => props.withBorder && StyledBaseModalHeaderBorder};
`;
export const StyledBaseModal = styled.div`
  min-width: 300px;
  overflow-y: initial;
  animation: ${({ theme }) => theme.animations.slide.modal} 0.2s ease-in-out
    forwards;

  & > ${StyledCard} {
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
  }
`;
export const StyledBaseModalWrapper = styled.div`
  display: flex;
  gap: 20px;
`;

const BaseModalComponent = ({
  header,
  footer,
  children,
  leftBlock,
  rightBlock,
  hasHeader = true,
  isBlocked = false,
  style,
  onClose,
  onCrossButtonClick,
}: BaseModalProps) => {
  const modalRef = useRef(null);
  const { closeModal } = useModalManager();
  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch(setDispatchModal(undefined));
    closeModal();
    onClose && onClose();
  };

  /** Закрыть модалку по нажатию на Esc */
  useEffect(() => {
    const eventHandler = (event: DocumentEventMap['keydown']) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', eventHandler);

    return () => {
      document.removeEventListener('keydown', eventHandler);
    };
  }, []);

  return (
    <StyledBaseModalWrapper ref={modalRef}>
      {leftBlock}
      <StyledBaseModal style={style}>
        <CardComponent>
          {hasHeader && (
            <StyledBaseModalHeader withBorder={!!header}>
              <StyledBaseModalHeaderTitle>{header}</StyledBaseModalHeaderTitle>
              {!isBlocked ? (
                <IconButtonComponent
                  onClick={onCrossButtonClick || handleClose}
                >
                  <CrossIcon />
                </IconButtonComponent>
              ) : (
                <div />
              )}
            </StyledBaseModalHeader>
          )}
          <StyledBaseModalContent>{children}</StyledBaseModalContent>
          {footer && <StyledBaseModalFooter>{footer}</StyledBaseModalFooter>}
        </CardComponent>
      </StyledBaseModal>
      {rightBlock}
    </StyledBaseModalWrapper>
  );
};

export default BaseModalComponent;
